/**
 * Invitation endpoints. Two audiences share this module: the invitee, who
 * previews and accepts a link before (or instead of) having an account, and the
 * island admin, who sends, lists, revokes and resends them.
 *
 * Failures arrive as an `ApiRequestError` carrying the backend's machine-
 * readable `code`, because the invite flow branches on it — `account_exists`
 * switches the landing page to login, `invitation_email_mismatch` keeps the user
 * on the form, `already_member` and `rate_limited` become admin-facing copy.
 */

import config from '@/env-config';
import { formatApiErrorMessage } from '@/utils/api-error';
import type { CreateInvitationInput } from '@shared/schemas/invitation.schema.js';
import type {
  InvitationAcceptResult,
  InvitationPreview,
  InvitationSummary
} from '@shared/types.js';
import type { AuthResponse } from '@/types/auth.types';

/** An API failure that kept the backend's `code`, so callers can branch on it. */
export class ApiRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('jwt_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };
}

async function request<T>(path: string, init: RequestInit, fallback: string): Promise<T> {
  const response = await fetch(`${config.api.baseUrl}${path}`, init);
  const body = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new ApiRequestError(
      formatApiErrorMessage(body, `${fallback}: ${response.status}`),
      response.status,
      typeof body?.code === 'string' ? body.code : undefined
    );
  }

  return body.data as T;
}

/** Public: what the landing page shows before anyone signs in. */
export async function previewInvitation(token: string): Promise<InvitationPreview> {
  return request<InvitationPreview>(
    `/invitations/${encodeURIComponent(token)}`,
    { method: 'GET', headers: { 'Content-Type': 'application/json' } },
    'Failed to load invitation'
  );
}

/** Redeem a link as the signed-in user. The email must match exactly. */
export async function acceptInvitation(token: string): Promise<InvitationAcceptResult> {
  return request<InvitationAcceptResult>(
    `/invitations/${encodeURIComponent(token)}/accept`,
    { method: 'POST', headers: authHeaders() },
    'Failed to accept invitation'
  );
}

export interface RegisterWithInvitationInput {
  token: string;
  username: string;
  password: string;
  avatar?: string;
}

/**
 * The only signup route. The email is not part of the body: it comes from the
 * invitation. The JWT is stored on success, exactly as login does.
 */
export async function registerWithInvitation(
  input: RegisterWithInvitationInput
): Promise<AuthResponse['data'] & { invitation: InvitationAcceptResult }> {
  const result = await request<AuthResponse['data'] & { invitation: InvitationAcceptResult }>(
    '/auth/register-with-invitation',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input)
    },
    'Registration failed'
  );

  if (result?.token) {
    localStorage.setItem('jwt_token', result.token);
  }

  return result;
}

/** Admin: invite an address to an island. */
export async function createInvitation(
  roomId: string,
  input: CreateInvitationInput
): Promise<InvitationSummary> {
  return request<InvitationSummary>(
    `/rooms/${roomId}/invitations`,
    { method: 'POST', headers: authHeaders(), body: JSON.stringify(input) },
    'Failed to send invitation'
  );
}

/** Admin: the island's pending invitations. */
export async function listInvitations(roomId: string): Promise<InvitationSummary[]> {
  const data = await request<{ invitations: InvitationSummary[] }>(
    `/rooms/${roomId}/invitations`,
    { method: 'GET', headers: authHeaders() },
    'Failed to load invitations'
  );
  return data?.invitations ?? [];
}

/** Admin: kill a pending link. */
export async function revokeInvitation(roomId: string, invitationId: string): Promise<void> {
  await request<void>(
    `/rooms/${roomId}/invitations/${invitationId}`,
    { method: 'DELETE', headers: authHeaders() },
    'Failed to revoke invitation'
  );
}

/** Admin: send it again with a new token and a fresh expiry. */
export async function resendInvitation(
  roomId: string,
  invitationId: string
): Promise<InvitationSummary> {
  return request<InvitationSummary>(
    `/rooms/${roomId}/invitations/${invitationId}/resend`,
    { method: 'POST', headers: authHeaders() },
    'Failed to resend invitation'
  );
}
